import { CreateStopInput, CreateTripInput, FlagWithTrip, Stop, StopTypeEnum, Trip, TripStatusEnum } from '@/models/gen/graphql';
import { Datetime, Validation, createNotification, getDiff, zeroPadFlightNumber } from '@/utils';
import { useCallback, useEffect, useMemo } from 'react';
import useOnChange, { OnChange } from '@/hooks/useOnChange';

import { EditTripsModalValidator } from '@/components/EditTripsModal/utils';
import { Toast } from '@/models';
import { TripViewProps } from '@/features/Trips/components/TripModal/views/trip';
import { createActivityForTrip } from '@/api/services/trips/createActivityBulk';
import { createComponentState } from '@/state';
import createFlagHasTripBulk from '@/api/services/trips/createFlagHasTripBulk';
import createStopBulk from '@/api/services/trips/createStopBulk';
import createTrips from '@/api/services/trips/createTrips';
import deleteFlagHasTripBulk from '@/api/services/trips/deleteFlagHasTripBulk';
import deleteStopBulk from '@/api/services/trips/deleteStopBulk';
import { getFlagByTripId } from '@/api/services/flag/SearchFlag';
import { getStopByTripId } from '@/api/services/stop/SearchStop';
import { getTripForModal } from '@/api/services/trips/searchTrips';
import updateStopBulk from '@/api/services/trips/updateStopBulk';
import updateTrackFlightBulk from '@/api/services/trips/updateTrackFlightBulk';
import updateTripBulk from '@/api/services/trips/updateTripBulk';
import { useClientIdsThatRequireCrew } from '@/api/services/providers/searchProviders';
import useUuid from '@/hooks/useUuid';

export type TripStop = Partial<Stop> & {
  airportFilter?: string;
};
type TripForm = Omit<Partial<Trip>, 'stops' | 'flightNumber'> & {
  scheduledDays?: string[];
  comments?: string;
  days?: number;
  increment?: boolean;
  doScheduledDatetime?: string;
  doAirportFilter?: string;
  flightNumber?: number | string;
  stops?: TripStop[];
};
export type UseTripViewState = {
  trip?: TripForm;
  original?: Partial<Trip>;
  loading?: boolean;
  loopOrFlightNumber?: string;
};
export const initUseTripsModalState: UseTripViewState = {
  trip: {
    status: TripStatusEnum.Active,
  },
  original: {},
  loading: false,
  loopOrFlightNumber: undefined,
};
export const useTripViewState = createComponentState(initUseTripsModalState);
const useTripView = ({ data, title, mode, selected, onHide, onSubmit }: TripViewProps, fileUploadRef?: any): UseTripViewValues => {
  const [state, setState] = useTripViewState(({ state, setState }) => [state, setState]);
  const { trip = {}, original = {}, loopOrFlightNumber } = state;
  const onChange = useOnChange(setState);
  const stopsDisabled = mode === 'editing' && selected?.length > 1;
  const [{ data: providersThatRequireCrew, loading: loadingCrewProviders }] = useClientIdsThatRequireCrew();

  const handleHide = async (): Promise<void> => {
    const isConfirmCancel: boolean = await onHide?.();
    if (isConfirmCancel) setState(initUseTripsModalState);
  };

  const handleSubmit = async (copy = false): Promise<void> => {
    if (trip?.id && copy === true) trip['copy'] = !!copy;
    trip.pilots = trip?.pilots || 0;
    trip.attendants = trip?.attendants || 0;
    trip.flightNumber = parseInt(`${trip?.flightNumber || 0}`);
    trip.loopName = trip?.loopName || '';
    if (trip?.id) {
      await handleUpdateTripBulk(trip, selected?.length > 1 ? selected || [] : [original]);
    } else {
      await handleCreateTripBulk(trip, fileUploadRef);
    }
    onSubmit?.(state);
  };
  const onSaveAndCopy = async (): Promise<void> => {
    trip.pilots = trip?.pilots || 0;
    trip.attendants = trip?.attendants || 0;
    trip.flightNumber = parseInt(`${trip?.flightNumber || 0}`);
    trip.loopName = trip?.loopName || '';
    await handleCreateTripBulk(trip, fileUploadRef);
    onSubmit?.(state, true);
  };

  const selectedCount = selected ? Object.values(selected).length : 0;
  const uuid = useUuid();

  const onAddStop = (): void => {
    setState((current: UseTripViewState): UseTripViewState => {
      const lastScheduled = (current?.trip?.stops || []).at(-1)?.scheduled || current?.trip?.scheduled;
      const scheduled = lastScheduled ? new Datetime(lastScheduled).add(5, 'minutes').toString() : null;
      return {
        ...current,
        trip: {
          ...(current?.trip || {}),
          stops: [
            ...(current?.trip?.stops || []),
            {
              id: new Date().getTime().toString(),
              type: StopTypeEnum.Pu,
              airportFilter: current?.trip?.airportCode,
              airportCode: current?.trip?.airportCode,
              scheduled,
            },
          ],
        },
      };
    });
  };
  const onBlurFlightNumber = (): void => {
    setState((current: UseTripViewState): UseTripViewState => {
      const value = (current?.loopOrFlightNumber || '').trim();
      // if there is no value
      if (!value) {
        return { ...current, loopOrFlightNumber: '', trip: { ...(current?.trip || {}), flightNumber: undefined, loopName: undefined } };
      }
      // checking if loop name
      if (value.length > 4 || value.match(/\D/g)) {
        return {
          ...current,
          loopOrFlightNumber: value,
          trip: { ...(current?.trip || {}), flightNumber: undefined, loopName: value },
        };
      }
      // if value is a valid flight number
      return {
        ...current,
        loopOrFlightNumber: value.replace(/^[0]+/, '').padStart(4, '0'),
        trip: { ...(current?.trip || {}), flightNumber: value, loopName: undefined },
      };
    });
  };

  const validity = useMemo(() => {
    if (mode === 'creating' && !EditTripsModalValidator.keys.includes('scheduledDays')) EditTripsModalValidator.keys.push('scheduledDays');
    if (mode === 'editing' && EditTripsModalValidator.keys.includes('scheduledDays'))
      EditTripsModalValidator.keys.splice(EditTripsModalValidator.keys.indexOf('scheduledDays'), 1);
    const result = (EditTripsModalValidator || (() => ({})))(trip, selected, providersThatRequireCrew);
    return result;
  }, [mode, providersThatRequireCrew, selected, trip]);
  const isValid = Validation.isValid(validity) && !loadingCrewProviders;

  const getTripData = useCallback(
    async (id: string): Promise<void> => {
      setState((current: UseTripViewState): UseTripViewState => ({ ...current, loading: true }));
      try {
        const trip = await getTripForModal(id);
        setState((current: UseTripViewState): UseTripViewState => ({ ...current, trip, original: trip }));
      } catch (error) {
        console.error(error);
      } finally {
        setState((current: UseTripViewState): UseTripViewState => ({ ...current, loading: false }));
      }
    },
    [setState]
  );

  const getAdditionalTripData = useCallback(
    async (id: string): Promise<void> => {
      setState((current: any): any => ({ ...current, loading: true }));
      try {
        // Run all the async functions concurrently
        const [flags, stops] = await Promise.all([getFlagByTripId(id), getStopByTripId(id)]);

        // Update the state once all the data is loaded
        setState((current: any): any => {
          const updatedState = {
            ...current,
            trip: { ...(current.trip || {}), flags, stops },
            original: { ...current.original, flags, stops },
          };
          return updatedState;
        });
      } catch (error) {
        console.error(error);
      } finally {
        setState((current: any): any => ({ ...current, loading: false }));
      }
    },
    [setState]
  );

  useEffect((): void => {
    if (typeof data === 'string') {
      getTripData(data);
      return;
    }
    if (mode === 'creating' || data?.id === trip?.id) return;
    setState((current) => ({
      ...current,
      trip: data || {},
      original: data || {},
      loopOrFlightNumber: data?.loopName || zeroPadFlightNumber(data?.flightNumber),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect((): void => {
    if (!data?.id) return;
    getAdditionalTripData(data?.id);
  }, [getAdditionalTripData, data?.id]);

  const requiresCrewInfo = loadingCrewProviders ? undefined : providersThatRequireCrew?.includes(trip?.payerProviderId || '');
  const returnState = {
    data,
    title,
    mode,
    trip,
    stopsDisabled,
    validity,
    loopOrFlightNumber,
    selectedCount,
    uuid,
    isValid,
    requiresCrewInfo,
  };
  const returnMethods = { onChange, setState, onSaveAndCopy, handleSubmit, handleHide, onBlurFlightNumber, onAddStop };
  return [returnState, returnMethods];
};

export type UseTripViewValues = [
  {
    data: Partial<Trip>;
    title: string;
    mode: 'creating' | 'editing';
    trip: TripForm;
    stopsDisabled: boolean;
    validity: Record<string, Validation.Validity>;
    loopOrFlightNumber: string;
    selectedCount: number;
    uuid: string;
    isValid: boolean;
    requiresCrewInfo: boolean | undefined;
  },
  {
    onChange: OnChange;
    setState: (value: UseTripViewState | ((current: UseTripViewState) => UseTripViewState)) => void;
    onSaveAndCopy: () => void;
    handleSubmit: (copy?: boolean) => void;
    handleHide: () => void;
    onBlurFlightNumber: () => void;
    onAddStop: () => void;
  },
];

const handleCreateTripBulk = async ({ stops = [], flags = [], scheduledDays = [], ...create }: any, fileUploadRef?: any): Promise<void> => {
  // ensure properties are the correct type
  create.fromManifest = create.fromManifest !== undefined ? !!create.fromManifest : true;
  if (create?.flightNumber) create.flightNumber = parseInt(create?.flightNumber);

  const filesToUpload = fileUploadRef?.current?.getFilesToUpload?.() ?? [];

  // create trip(s)
  const time = new Datetime(create?.scheduled || '').fullTime || '00:00:00';
  const createTripsPayload: CreateTripInput[] = scheduledDays.map((day: string): CreateTripInput => {
    const scheduled = new Datetime(day).setTime(time).toString();
    return {
      ...create,
      scheduled,
      documents: filesToUpload.length > 0 ? filesToUpload.map((file) => ({ ...file, tripId: '', fileLocation: '' })) : undefined,
    };
  });

  const response = await createTrips(createTripsPayload);

  let successCount = 0;
  let totalDocumentsUploaded = 0;

  if (!response?.length) {
    console.error('handleCreateTripBulk: no response from createTrip');
    return;
  }

  for (let i = 0; i < response.length; i++) {
    const error: string = response[i]?.error;
    if (error) {
      createNotification(error, Toast.Type.DANGER, 'Create Trip');
      continue;
    }
    successCount++;
    const node = response[i]?.node;

    const tripId: string = node?.id;
    const tripScheduled: string = node?.scheduled;

    // create stops if any were added
    if ((stops || []).length) {
      const createStopsPayload: CreateStopInput[] = [];
      (stops || []).forEach((stop: CreateStopInput): CreateStopInput => {
        if (!stop?.scheduled) {
          console.error('handleCreateTripBulk: no scheduled date for stop');
          return;
        }
        const time = new Datetime(stop?.scheduled).fullTime;
        const scheduled = new Datetime(tripScheduled).setTime(time).toString();
        createStopsPayload.push({ ...stop, scheduled, type: stop.type || StopTypeEnum.Do, tripId });
      });

      // call create stops
      if (createStopsPayload.length > 0) createStopBulk(createStopsPayload);
    }

    // create flags
    if (flags.filter(Boolean).length) {
      const createFlags: any = flags.filter(Boolean).map((flagId: string): any => ({ flagId, tripId }));
      createFlagHasTripBulk(createFlags);
    }

    const documentsData = node?.document;
    if (documentsData && filesToUpload.length > 0) {
      await fileUploadRef?.current?.performDeferredUploads?.(documentsData, true);
      totalDocumentsUploaded += documentsData.length;
    }
  }

  if (successCount > 0) {
    let message = 'Successfully created trip(s)';

    if (totalDocumentsUploaded > 0) {
      message += ' with document(s)';
    }

    createNotification(message, Toast.Type.SUCCESS, 'Create Trip');
  }
};

const handleUpdateTripBulk: any = async ({ copy = false, ...updateObj }: any, selected: Trip[]): Promise<void> => {
  const { increment = false, days = 0, stops = [], flags: updateFlags = [], comments, trackFlight, ...update } = updateObj;

  // stops are going to be handled seperately
  const { stops: originalStops, trackFlight: originalTrackFlight, ...original }: any = selected?.[0] || {};
  const [partial] = getDiff(original, update);

  if (copy) return cloneTrips({ partial, increment, days }, selected);

  // Update Trips Flow
  if (Object.values(partial).length > 0) {
    const bundle = [];
    const trips = selected;
    for (let t = 0; t < trips.length; t++) {
      const tripId = trips[t]?.id;
      const initTrip = trips[t];
      //apply changes to all selected trips
      const output: Trip = { ...partial, id: tripId };
      if (increment) output.scheduled = new Datetime(initTrip?.scheduled).add(days, 'days').toString();
      if (output?.flightNumber) output.flightNumber = parseInt(output?.flightNumber + '');
      bundle.push(output);
    }

    await updateTripBulk(bundle);
  }

  // Handle Update Track Flight Flow
  const [partialTrackFlight] = getDiff(originalTrackFlight, trackFlight);
  if (original.trackFlightId && Object.values(partialTrackFlight).length > 0) {
    await updateTrackFlightBulk([{ ...partialTrackFlight, trackerProvider: 'portal', id: original.trackFlightId }]);
  }

  // Handle Crud Flags Flow
  if (selected.length < 2) {
    const flags = updateFlags?.map((node: any): string => node.id || node);
    const createFlagsPayload = [];
    const deleteFlagsPayload = [];
    selected.forEach((trip: Trip): void => {
      const originalFlags: FlagWithTrip[] = trip.flags || [];

      // Check update flags to see if the original flags have it already
      flags?.forEach((flagId: string): void => {
        const foundFlag = !!originalFlags.filter((flag: FlagWithTrip): boolean => flag.id === flagId).length;
        if (!foundFlag) createFlagsPayload.push(flagId);
      });

      // Check original flags to see if it's removed from the update flags
      originalFlags.forEach((flag: FlagWithTrip): void => {
        const foundFlag: boolean = !!flags.filter((flagId: string): boolean => flag.id === flagId).length;
        if (!foundFlag) deleteFlagsPayload.push(flag.id);
      });
    });

    // call Flag CRUD
    if (createFlagsPayload.length > 0 || deleteFlagsPayload.length > 0)
      handleSubmitFlag({ updates: { creates: createFlagsPayload, deletes: deleteFlagsPayload }, tripId: update.id });
  }

  // Handle Crud Stops Flow
  if (selected.length === 1) {
    const invalid: string[] = [];

    // Create Stops
    const createStopsPayload = (stops || [])
      .filter((node: any): boolean => !Validation.isValidUUID(node.id))
      .map((stop: any): any => {
        // validate stop
        ['locationId', 'scheduled'].forEach((field) => {
          if (!stop[field]) {
            if (!invalid.includes(field)) invalid.push(field);
          }
        });
        return { ...stop, scheduled: new Datetime(stop?.scheduled).toString(), type: stop.type || 'DO', tripId: original.id };
      });

    if (invalid.length > 0) {
      createNotification(`Invalid fields: ${invalid.sort().join(', ')}`, Toast.Type.DANGER, 'Create Stops');
      return;
    }

    // Call Create Stops
    if (createStopsPayload.length > 0) await createStopBulk(createStopsPayload);

    // Update Stops
    const updateStopsPayload = [];
    (stops || [])
      .filter((node: any): boolean => Validation.isValidUUID(node.id))
      .forEach((stop: Stop): void => {
        const initStop = (originalStops || []).filter((node: Stop): boolean => node.id === stop.id)[0];
        const [partial] = getDiff(initStop, stop);
        if (Object.values(partial).length) updateStopsPayload.push({ ...partial, id: stop.id });
      });

    // Call Update Stops
    if (updateStopsPayload.length > 0) await updateStopBulk(updateStopsPayload);

    // Delete Stops
    const deleteStopsPayload = (originalStops || [])
      .filter((node: any): boolean => !(stops || []).filter((el: any): boolean => (el.id || el) === node.id).length)
      .map((node: any): string => node.id);

    // Call Delete Stops
    if (deleteStopsPayload.length > 0) await deleteStopBulk(deleteStopsPayload);
  }

  // Handle Create Comments
  if (comments?.length) {
    const createCommentsPayload: Array<{ tripId: string; comment: string; field: string }> = selected.map(
      ({ id: tripId }: Trip): { tripId: string; comment: string; field: string } => ({
        field: 'Comment',
        comment: comments,
        tripId,
      })
    );
    await createActivityForTrip(createCommentsPayload);
  }
};

const cloneTrips = async (
  { increment = false, days = 0, partial }: { partial: Trip; days: number; increment: boolean },
  selected: Trip[]
): Promise<void> => {
  // Cloned Trips
  const createTripsPayload: CreateTripInput[] = Object.values(selected).map((trip: any): CreateTripInput => {
    const result: CreateTripInput = { ...trip, ...partial };
    const scheduled = new Datetime(trip?.scheduled);
    if (increment) scheduled.add(days, 'days');
    result.scheduled = scheduled.toString();
    result.companyId = '';
    result.fromManifest = result.fromManifest !== undefined ? !!result.fromManifest : true;
    return result;
  });
  const response = await createTrips(createTripsPayload);
  for (let i: number = 0; i < response.length; i++) {
    const error: string = response[i].error;
    if (error) {
      createNotification(
        `Trip for flight ${createTripsPayload[i].flightNumber || createTripsPayload[i].loopName}: ${error}`,
        Toast.Type.DANGER,
        'Create Trip'
      );
      continue;
    } else {
      createNotification(
        `Trip for flight ${createTripsPayload[i].flightNumber || createTripsPayload[i].loopName} successfully created`,
        Toast.Type.SUCCESS,
        'Create Trip'
      );
    }
  }
};

// Handle Adding Flag to Trip
const handleSubmitFlag = async ({ updates: { creates, deletes }, tripId }: any): Promise<void> => {
  if (!creates?.length && !deletes?.length) {
    return createNotification('no changes made', Toast.Type.WARNING, 'Assign Flag to Trip');
  }
  if (creates?.length) {
    await createFlagHasTripBulk(creates.map((flagId: string): { flagId: string; tripId: string } => ({ flagId, tripId })));
  }
  if (deletes?.length) {
    await deleteFlagHasTripBulk(deletes.map((flagId: string): { flagId: string; tripId: string } => ({ flagId, tripId })));
  }
};

export default useTripView;
